import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { type LotteryRewardName } from '@/common/types/lottery';
import { Tab } from '@/components/Tabs/Tab';
import { Tabs } from '@/components/Tabs/Tabs';
import { TabsNavigation } from '@/components/Tabs/TabsNavigation';
import { StringMain } from '@/components/Typography/Typography';
import { WaitInfo } from '@/components/ui/WaitInfo/WaitInfo';
import { selectLastLotteryWinner } from '@/redux/selectors/lotterySelector';
import { useAppSelector } from '@/redux/store';

import { useCurrentLotteryTier } from '../hooks';
import { LotteryBgWrapper } from '../LotteryBgWrapper/LotteryBgWrapper';
import { Title } from '../LotteryContainer/Title/Title';
import { RewardValue } from '../RewardValue/RewardValue';
import { MyTicketListForCurrentWinner } from '../Tickets/MyTicketListForCurrentWinner/MyTicketListForCurrentWinner';
import { WinnersList } from '../WinnersList/WinnersList';

import { CountOfMatches } from './CountOfMatches/CountOfMatches';
import { WinnerTicket } from './WinnerTicket/WinnerTicket';

import css from './styles.module.scss';

const LotteryInProgress: FC = () => {
  const { t } = useTranslation();
  const type = useCurrentLotteryTier();
  const isDrawn = useAppSelector(
    (state) => state.lottery.inDraw.find(({ tier }) => tier === type)?.drawn,
  );
  const lastWinnerData = useAppSelector(selectLastLotteryWinner);
  const tabList = [
    { id: '1', label: 'Winners' },
    { id: '2', label: 'My Tickets' },
  ];

  const rewardName = lastWinnerData
    ? Object.keys(lastWinnerData.drawResult)[0]
    : undefined;
  const rewardValue =
    rewardName && lastWinnerData
      ? lastWinnerData.drawResult[
          rewardName as keyof typeof lastWinnerData.drawResult
        ]
      : undefined;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full h-full"
    >
      <LotteryBgWrapper type={type}>
        <AnimatePresence>
          {lastWinnerData && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className={css.lotteryInProgress}
            >
              <Title className="mb-6" />
              {isDrawn ? (
                <div className="flex flex-1 items-center">
                  <WaitInfo
                    title={{ text: t('The ticket draw is over') }}
                    description={{
                      text: t(
                        'Prizes are being credited… Thank you for your patience!',
                      ),
                      className: 'max-w-[13rem]',
                    }}
                  />
                </div>
              ) : (
                <>
                  {rewardName && rewardValue && (
                    <RewardValue
                      value={rewardValue}
                      reward={rewardName as LotteryRewardName}
                      className="mb-6"
                    />
                  )}
                  <StringMain className="!text-white/80 mb-3">
                    {t(`${lastWinnerData.drawRank}th place:`)}
                  </StringMain>
                  {lastWinnerData.drawRank && (
                    <WinnerTicket
                      place={lastWinnerData.drawRank}
                      className="mb-5"
                    />
                  )}
                  <CountOfMatches className="mb-6" />
                </>
              )}
              <Tabs initActiveTab="1">
                <TabsNavigation list={tabList} />
                <Tab
                  alwaysStayInDom={true}
                  tabId="1"
                  className="h-[43vh] overflow-y-auto hidden-scrollbar w-full pt-2"
                >
                  <WinnersList />
                </Tab>
                <Tab
                  alwaysStayInDom={true}
                  tabId="2"
                  className="h-[43vh] overflow-y-auto hidden-scrollbar w-full pt-2"
                >
                  <MyTicketListForCurrentWinner />
                </Tab>
              </Tabs>
            </motion.div>
          )}
        </AnimatePresence>
      </LotteryBgWrapper>
    </motion.div>
  );
};

export { LotteryInProgress };
